import { createSelector } from '@reduxjs/toolkit';
import type { RootState } from '../../store';
import type { SessionId } from '../domain/Session';
import { sessionByIdQuery, sessionsQuery } from '../sessions/queries';

const slice = (state: RootState) => state.networkMap;

export const networkMapQuery = createSelector(slice, (state) => state.network);
export const sessionNetworkQuery = (sessionId: SessionId) =>
    createSelector(slice, (state) => state.sessionNetworks[sessionId] ?? null);
export const networkMapShouldRefreshQuery = createSelector(slice, (state) => state.networkExecutionsNeedRefresh);
export const networkExecutionStatusesQuery = createSelector(slice, (state) => state.networkExecutionStatuses);
export const allSessionsAreShownQuery = createSelector(slice, (state) => state.sessionsShownOnMap.length > 0);
export const selectedGradeReferenceQuery = createSelector(slice, (state) => state.selectedGradeReference);
export const displayPrQuery = createSelector(slice, (state) => state.displayPr);
export const displayRoadsQuery = createSelector(slice, (state) => state.displayRoads);
export const displayDegradationsQuery = createSelector(slice, (state) => state.displayDegradations);
export const selectedmapStyleQuery = createSelector(slice, (state) => state.selectedMapStyle);
export const currentMapBoundsQuery = createSelector(slice, (state) => state.currentMapBounds);
export const requestedMapBoundsQuery = createSelector(slice, (state) => state.requestedMapBounds);
export const sessionsShownQuery = createSelector(slice, (state) => state.sessionsShownOnMap);
export const isSessionShownQuery = (sessionId: SessionId) =>
    createSelector(sessionsShownQuery, (sessionsShownOnMap) => sessionsShownOnMap.includes(sessionId));
export const renderedSessionsIdsQuery = createSelector(slice, (state) => state.renderedSessionsIds);
export const highlightedRoadSectionLineQuery = createSelector(slice, (state) => state.highlightedRoadSectionLine);

export const initialBoundsQuery = (sessionId: SessionId | null) => (
    state: RootState,
): [number, number, number, number] | null => {
    if (sessionId) {
        const session = sessionByIdQuery(sessionId)(state);
        if (session) {
            return session.bbox;
        }
    }

    const currentMapBounds = currentMapBoundsQuery(state);
    if (currentMapBounds) {
        return [
            currentMapBounds.getWest(),
            currentMapBounds.getSouth(),
            currentMapBounds.getEast(),
            currentMapBounds.getNorth(),
        ];
    }

    const sessions = sessionsQuery(state);
    const sessionsWithBBox = sessions.filter((session) => session.bbox !== null);
    if (sessionsWithBBox.length > 0) {
        return [
            Math.min(...sessionsWithBBox.map((session) => session.bbox![0])),
            Math.min(...sessionsWithBBox.map((session) => session.bbox![1])),
            Math.max(...sessionsWithBBox.map((session) => session.bbox![2])),
            Math.max(...sessionsWithBBox.map((session) => session.bbox![3])),
        ];
    }

    return null;
};
